import { analytics } from '@play-co/gcinstant';
import { user } from 'src/StateObserver';
import { GCInstant } from 'src/lib/gcinstant';
import { captureGenericError } from 'src/lib/sentry';
import { duration } from 'src/replicant/utils/duration';
import { type ConsentSurface } from './onboarding.analytics';
import { type EntryFirstSubscribeReason } from './entry.analytics.schema';

/**
 * The surfaces inside the spec's trigger window — "during the splash screen or
 * on the immediate next screen". Pre-entry detection and the first-entry
 * onboarding popups all run on the `splash_screen` surface; every later
 * surface is outside the window on purpose (kept below, commented out, to make
 * the exclusion explicit).
 */
const ENTRY_WINDOW_SURFACES: readonly ConsentSurface[] = [
  'splash_screen',
  // 'launch_sequence',
  // 'post_tutorial',
  // 'level_5',
  // 'level_complete',
];

/**
 * Threshold for `preEntry`: when the user state is older than this at the
 * moment of the first login, it was created by the OA follow webhook well
 * before the entry, so the subscription is NOT entry-driven. Both fresh cases
 * stay far under the window: a traditional-consent follow lands only seconds
 * before login (consent screen → follow webhook → game load → login), and a
 * state created at the login itself differs from `lastLoginAt` by a few ms at
 * most (play-co/replicant#2810).
 *
 * Known imprecision: a state can also be pre-created WITHOUT a subscription —
 * e.g. another player fetching a nonexistent user via `api.fetchStates`
 * (persisted by Replicant's createIfNotFound fallback). Such users don't fire
 * this event by themselves, but if one later subscribes freshly at their
 * first entry, the old `createdAt` misclassifies the subscription as
 * pre-existing. Accepted: the case is rare and errs toward EXCLUDING users
 * from entry-driven counts. The precise upgrade path, if ever needed, is
 * Replicant's `chatbotMetainfo.timeFirstSubscribed` (maintained by the LINE
 * follow webhooks; requires a small core API to expose to the client).
 */
const PRE_EXISTING_SUBSCRIPTION_THRESHOLD = duration({ minutes: 60 });

let fired = false;

/** Test-only: clear the fire-once guard so test cases stay isolated. */
export function resetEntryFirstSubscribeForTesting(): void {
  fired = false;
}

/**
 * Whether the OA subscription predates this entry rather than being driven by
 * it. The splash popups subscribe in-session by definition; for `preEntry`
 * (already a friend when the session started) the user state's age tells the
 * two apart — see {@link PRE_EXISTING_SUBSCRIPTION_THRESHOLD}.
 */
function getIsPreExistingSubscription(
  subscribeReason: EntryFirstSubscribeReason,
): boolean {
  if (subscribeReason !== 'preEntry') return false;

  const { createdAt, lastLoginAt, loginCount } = user();

  // A previous session existed, so the subscription predates this entry.
  if (loginCount > 1) return true;

  // First-ever login: `lastLoginAt` is the timestamp the server stamped for
  // THIS login, so its gap from `createdAt` is the state's age at entry. The
  // `!` is safe: `lastLoginAt` is undefined only for a never-logged-in
  // (webhook-created) user, who by definition isn't running game code here.
  return lastLoginAt! - createdAt > PRE_EXISTING_SUBSCRIPTION_THRESHOLD;
}

/**
 * Track `EntryFirstSubscribe` — fired once per user, the moment we confirm the
 * user is subscribed to the OA during the entry window of their FIRST session
 * (LINE "TFR" conversion). Carries all `EntryFinal` event properties verbatim
 * plus `subscribeReason`, so it segments exactly like `EntryFinal` for both
 * traditional and simplified consent. See #11318.
 *
 * Call sites report "the user is confirmed subscribed via X on surface Y";
 * eligibility is owned here, so this is safe to call from any surface in any
 * session:
 * - no-ops for surfaces outside the spec window ("during the splash screen or
 *   on the immediate next screen") — the level-win asks are excluded;
 * - reports to Sentry and no-ops if called before `EntryFinal` was sent —
 *   every subscribe surface is sequenced after it, so that's a bug that would
 *   silently lose a conversion;
 * - no-ops unless that `EntryFinal` had `first=true` (the spec's trigger);
 * - no-ops after the first fire (the subscribe surfaces already self-skip for
 *   subscribed users, this is the backstop).
 */
export function trackEntryFirstSubscribe(
  subscribeReason: EntryFirstSubscribeReason,
  origin: ConsentSurface,
): void {
  if (fired) return;
  if (!ENTRY_WINDOW_SURFACES.includes(origin)) return;

  const entryFinalProps = GCInstant.entryFinalEventProperties;
  if (!entryFinalProps) {
    captureGenericError(
      `trackEntryFirstSubscribe('${subscribeReason}') called before EntryFinal was sent`,
      null,
    );
    return;
  }
  if (!entryFinalProps.first) return;

  fired = true;
  const isPreExistingSubscription =
    getIsPreExistingSubscription(subscribeReason);

  // GCInstant owns delivery: it always tracks to Amplitude, and routes to the
  // marketing conversion trackers (LINE Conversion API, LY Ads) only when
  // `isPreExistingSubscription` is false. Eligibility stays here.
  analytics.game.entry.trackEntryFirstSubscribe({
    // EntryFinal props pass through verbatim per spec; they are not re-declared
    // in the schema, hence the widening to `object` for the spread.
    ...(entryFinalProps as object),
    subscribeReason,
    isPreExistingSubscription,
  });
}
